/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import classNames from "classnames";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { createUseStyles } from "react-jss";
import { DragListComponentProps } from "../../interface";
import NoDataSvg from "../NoData";

type ListId = "available" | "selected";

// Swap two array slots in place. Guards keep it safe under noUncheckedIndexedAccess
// (out-of-range indexes are simply ignored).
const swapInPlace = <T,>(arr: T[], a: number, b: number) => {
  const first = arr[a];
  const second = arr[b];
  if (first === undefined || second === undefined) return;
  arr[a] = second;
  arr[b] = first;
};

// Local palette for this component's react-jss rules. `surfaceWhite` uses the shared design token;
// the rest are the layout picker's greys/blues, named once here instead of repeated as raw hex
// across a dozen rules (keeps them consistent and swappable).
const COLORS = {
  text: "#192048",
  textMuted: "#666a84",
  border: "#dce8f5",
  selectedBg: "#e8f1fc",
  hoverBg: "#d6e6fa",
  accent: "#415cab",
  surfaceWhite: "var(--surface-white, #ffffff)",
  shadow: "0.25rem 0.25rem 0.9375rem 0 rgba(65, 92, 171, 0.15)",
  rowHoverBg: "rgba(65, 92, 171, 0.08)",
} as const;

const useStyles = createUseStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    width: "100%",
  },
  hint: {
    fontSize: "0.875rem",
    lineHeight: 1.2,
    color: COLORS.text,
  },
  lists: {
    display: "flex",
    alignItems: "stretch",
    gap: "1.5rem",
    width: "100%",
  },
  column: {
    flex: "1 1 0",
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
  },
  header: {
    display: "flex",
    alignItems: "center",
    minHeight: "2.5rem",
    fontSize: "1rem",
    fontWeight: 600,
    lineHeight: 1.2,
    color: COLORS.text,
  },
  search: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    height: "2rem",
    padding: "0 0.5rem",
    border: `1px solid ${COLORS.textMuted}`,
    borderRadius: "0.25rem",
    background: COLORS.surfaceWhite,
    "&:focus-within": {
      borderColor: COLORS.text,
    },
    "& svg": {
      flex: "0 0 auto",
    },
    "& input": {
      flex: "1 1 auto",
      minWidth: 0,
      border: "none",
      outline: "none",
      background: "transparent",
      fontSize: "0.875rem",
      lineHeight: 1.2,
      color: COLORS.text,
      "&::placeholder": {
        color: COLORS.textMuted,
      },
    },
  },
  searchClear: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    border: "none",
    background: "transparent",
    color: COLORS.textMuted,
    cursor: "pointer",
  },
  listBox: {
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${COLORS.border}`,
    borderRadius: "0.5rem",
    background: COLORS.surfaceWhite,
    boxShadow: COLORS.shadow,
    overflowY: "auto",
    overflowX: "hidden",
  },
  emptyState: {
    // Frame for the empty-state illustration (fixed ~300px). Fills and centres it, and clips instead
    // of scrolling so a short box doesn't show a scrollbar; max-* rules let the image scale down.
    flex: "1 1 0",
    minHeight: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    padding: "1rem",
    "& > div": {
      minHeight: 0,
      maxHeight: "100%",
      maxWidth: "100%",
    },
    "& img": {
      minHeight: 0,
      maxHeight: "100%",
      maxWidth: "100%",
      objectFit: "contain",
    },
  },
  listItem: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    minHeight: "3rem",
    padding: "0 1rem",
    fontSize: "0.875rem",
    lineHeight: 1.2,
    color: COLORS.text,
    cursor: "grab",
    userSelect: "none",
    "&:hover": {
      backgroundColor: COLORS.rowHoverBg,
    },
    "&.selected": {
      backgroundColor: COLORS.selectedBg,
    },
    "&.dragging": {
      opacity: 0.5,
    },
    "&.dragOver": {
      boxShadow: `inset 0 0.125rem 0 0 ${COLORS.accent}`,
    },
  },
  itemText: {
    flex: "1 1 auto",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  buttonColumn: {
    flex: "0 0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "1.5rem",
  },
  iconButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "2rem",
    height: "2rem",
    padding: "0.25rem",
    border: "none",
    borderRadius: "0.25rem",
    background: COLORS.selectedBg,
    color: COLORS.text,
    cursor: "pointer",
    transition: "background-color 0.15s ease, opacity 0.15s ease",
    "&:hover:not(:disabled)": {
      backgroundColor: COLORS.hoverBg,
    },
    "&:disabled": {
      cursor: "not-allowed",
      opacity: 0.5,
    },
  },
});

const Chevron: FC<{ dir: "up" | "down" | "left" | "right" }> = ({ dir }) => {
  const points = {
    up: "6.5 14.5 12 9 17.5 14.5",
    down: "6.5 9.5 12 15 17.5 9.5",
    left: "14.5 6.5 9 12 14.5 17.5",
    right: "9.5 6.5 15 12 9.5 17.5",
  }[dir];
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <polyline points={points} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const DoubleChevron: FC<{ dir: "left" | "right" }> = ({ dir }) => {
  const sets =
    dir === "right"
      ? ["5 6.5 10.5 12 5 17.5", "11.5 6.5 17 12 11.5 17.5"]
      : ["13 6.5 7.5 12 13 17.5", "19 6.5 13.5 12 19 17.5"];
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {sets.map((p) => (
        <polyline
          key={p}
          points={p}
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
};

const SearchGlyph: FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="7" cy="7" r="5" stroke={COLORS.textMuted} strokeWidth="1.5" />
    <line x1="10.8" y1="10.8" x2="14" y2="14" stroke={COLORS.textMuted} strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const ClearGlyph: FC = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <line x1="4" y1="4" x2="12" y2="12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="12" y1="4" x2="4" y2="12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

function DragListComponent<T extends Record<string, unknown>>(props: DragListComponentProps<T>) {
  const { t } = useTranslation();
  const classes = useStyles();

  const {
    showButtons = false,
    allowMultiSelect = false,
    showSearch = false,
    availableHeader = t("Available"),
    selectedHeader = t("Selected"),
    searchPlaceholder = t("Search"),
    showHint = true,
    listHeight = "30rem",
    fillHeight = false,
    noItemsText = t("No items found"),
  } = props;

  const textField = props.fields?.text || "text";
  const valueField = props.fields?.value;
  const getText = useCallback((item: T): unknown => item?.[textField], [textField]);

  // Click-to-select only makes sense when there is something to act on (the
  // transfer buttons) or when the caller explicitly asks for multi-select.
  const selectable = allowMultiSelect || showButtons;

  // Selection is tracked by object reference so it survives reordering, filtering
  // and cross-list moves without relying on (possibly non-unique) value fields.
  const [selectedAvailable, setSelectedAvailable] = useState<Set<T>>(() => new Set<T>());
  const [selectedSelected, setSelectedSelected] = useState<Set<T>>(() => new Set<T>());
  const [activeList, setActiveList] = useState<ListId | null>(null);
  const [queryAvailable, setQueryAvailable] = useState("");
  const [querySelected, setQuerySelected] = useState("");

  const dragItem = useRef<{ listId: ListId; item: T } | null>(null);
  const [dragOverItem, setDragOverItem] = useState<T | null>(null);

  const listData = useCallback(
    (id: ListId): T[] => (id === "available" ? props.data1 || [] : props.data2 || []),
    [props.data1, props.data2],
  );
  const setListData = useCallback(
    (id: ListId, value: T[]) => (id === "available" ? props.setData1?.(value) : props.setData2?.(value)),
    [props.setData1, props.setData2],
  );
  const selectionOf = (id: ListId) => (id === "available" ? selectedAvailable : selectedSelected);
  const setSelectionOf = (id: ListId) => (id === "available" ? setSelectedAvailable : setSelectedSelected);
  const queryOf = (id: ListId) => (id === "available" ? queryAvailable : querySelected);

  const filterItems = useCallback(
    (data: T[], query: string) =>
      !query.trim()
        ? data
        : (data || []).filter((it) =>
            String(getText(it) ?? "")
              .toLowerCase()
              .includes(query.trim().toLowerCase()),
          ),
    [getText],
  );

  // Drop selection references that the parent has removed from the data, while
  // keeping the same Set instance when nothing changed (avoids render loops and
  // preserves selection across our own reorder/move updates).
  useEffect(() => {
    setSelectedAvailable((prev) => {
      const next = new Set([...prev].filter((it) => (props.data1 || []).includes(it)));
      return next.size === prev.size ? prev : next;
    });
  }, [props.data1]);
  useEffect(() => {
    setSelectedSelected((prev) => {
      const next = new Set([...prev].filter((it) => (props.data2 || []).includes(it)));
      return next.size === prev.size ? prev : next;
    });
  }, [props.data2]);

  const handleItemClick = useCallback(
    (e: React.MouseEvent, listId: ListId, item: T) => {
      if (!selectable) return;
      setActiveList(listId);
      const setSelection = setSelectionOf(listId);
      setSelection((prev) => {
        const next = new Set(prev);
        if (allowMultiSelect) {
          if (next.has(item)) next.delete(item);
          else next.add(item);
        } else {
          next.clear();
          next.add(item);
        }
        return next;
      });
    },
    [selectable, allowMultiSelect],
  );

  // --- moves -------------------------------------------------------------
  const transfer = useCallback(
    (items: T[], fromId: ListId, toId: ListId) => {
      if (!items.length) return;
      const moveSet = new Set(items);
      setListData(
        fromId,
        listData(fromId).filter((it) => !moveSet.has(it)),
      );
      setListData(toId, [...listData(toId), ...items]);
    },
    [listData, setListData],
  );

  const moveWithPosition = useCallback(
    (item: T, fromId: ListId, toId: ListId, beforeItem: T | null) => {
      // Dropping an item onto itself is a no-op. Without this guard the same-list branch splices the
      // item out, then indexOf(beforeItem) returns -1 (beforeItem IS the item we just removed) and it
      // gets re-appended to the bottom — silently reordering the list on a gesture meant to change nothing.
      if (beforeItem === item) return;
      if (fromId === toId) {
        const arr = [...listData(fromId)];
        const from = arr.indexOf(item);
        if (from < 0) return;
        arr.splice(from, 1);
        let to = beforeItem ? arr.indexOf(beforeItem) : arr.length;
        if (to < 0) to = arr.length;
        arr.splice(to, 0, item);
        setListData(fromId, arr);
      } else {
        const fromArr = listData(fromId).filter((it) => it !== item);
        const toArr = [...listData(toId)];
        let to = beforeItem ? toArr.indexOf(beforeItem) : toArr.length;
        if (to < 0) to = toArr.length;
        toArr.splice(to, 0, item);
        setListData(fromId, fromArr);
        setListData(toId, toArr);
      }
    },
    [listData, setListData],
  );

  const reorder = useCallback(
    (id: ListId | null, direction: "up" | "down") => {
      if (!id) return;
      const data = [...listData(id)];
      const selection = selectionOf(id);
      const indexes = data.map((it, i) => (selection.has(it) ? i : -1)).filter((i) => i >= 0);
      if (!indexes.length) return;
      if (direction === "up") {
        if (indexes[0] === 0) return;
        indexes.forEach((i) => swapInPlace(data, i - 1, i));
      } else {
        if (indexes[indexes.length - 1] === data.length - 1) return;
        [...indexes].reverse().forEach((i) => swapInPlace(data, i, i + 1));
      }
      setListData(id, data);
    },
    [listData, selectionOf, setListData],
  );

  const moveSelected = useCallback(
    (fromId: ListId, toId: ListId) => {
      const selection = selectionOf(fromId);
      const items = listData(fromId).filter((it) => selection.has(it));
      transfer(items, fromId, toId);
      setSelectionOf(fromId)(new Set<T>());
    },
    [listData, selectionOf, setSelectionOf, transfer],
  );

  const moveAll = useCallback(
    (fromId: ListId, toId: ListId) => {
      // Respect the active search filter — only move what is visible.
      const items = filterItems(listData(fromId), queryOf(fromId));
      transfer(items, fromId, toId);
      setSelectionOf(fromId)(new Set<T>());
    },
    [filterItems, listData, queryOf, setSelectionOf, transfer],
  );

  // --- drag & drop -------------------------------------------------------
  const handleDragStart = useCallback((listId: ListId, item: T) => {
    dragItem.current = { listId, item };
  }, []);
  const handleItemDragOver = useCallback((e: React.DragEvent, item: T) => {
    e.preventDefault();
    setDragOverItem(item);
  }, []);
  const handleListDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);
  const handleDropOnItem = useCallback(
    (e: React.DragEvent, targetId: ListId, targetItem: T) => {
      e.preventDefault();
      e.stopPropagation();
      if (dragItem.current) moveWithPosition(dragItem.current.item, dragItem.current.listId, targetId, targetItem);
      dragItem.current = null;
      setDragOverItem(null);
    },
    [moveWithPosition],
  );
  const handleDropOnList = useCallback(
    (e: React.DragEvent, targetId: ListId) => {
      e.preventDefault();
      if (dragItem.current) moveWithPosition(dragItem.current.item, dragItem.current.listId, targetId, null);
      dragItem.current = null;
      setDragOverItem(null);
    },
    [moveWithPosition],
  );
  const handleDragEnd = useCallback(() => {
    dragItem.current = null;
    setDragOverItem(null);
  }, []);

  // --- button enabled state ---------------------------------------------
  const visibleAvailable = filterItems(listData("available"), queryAvailable);
  const visibleSelected = filterItems(listData("selected"), querySelected);
  const activeData = activeList ? listData(activeList) : [];
  const activeSelection = activeList ? selectionOf(activeList) : new Set<T>();
  const activeIndexes = activeData.map((it, i) => (activeSelection.has(it) ? i : -1)).filter((i) => i >= 0);

  const buttons = [
    {
      key: "up",
      title: t("Move up"),
      node: <Chevron dir="up" />,
      disabled: activeIndexes.length === 0 || activeIndexes[0] === 0,
      onClick: () => reorder(activeList, "up"),
    },
    {
      key: "move-all-right",
      title: t("Add all"),
      node: <DoubleChevron dir="right" />,
      disabled: visibleAvailable.length === 0,
      onClick: () => moveAll("available", "selected"),
    },
    {
      key: "move-right",
      title: t("Add selected"),
      node: <Chevron dir="right" />,
      disabled: selectedAvailable.size === 0,
      onClick: () => moveSelected("available", "selected"),
    },
    {
      key: "move-left",
      title: t("Remove selected"),
      node: <Chevron dir="left" />,
      disabled: selectedSelected.size === 0,
      onClick: () => moveSelected("selected", "available"),
    },
    {
      key: "move-all-left",
      title: t("Remove all"),
      node: <DoubleChevron dir="left" />,
      disabled: visibleSelected.length === 0,
      onClick: () => moveAll("selected", "available"),
    },
    {
      key: "down",
      title: t("Move down"),
      node: <Chevron dir="down" />,
      disabled: activeIndexes.length === 0 || activeIndexes[activeIndexes.length - 1] === activeData.length - 1,
      onClick: () => reorder(activeList, "down"),
    },
  ];

  // --- render ------------------------------------------------------------
  const renderItem = (listId: ListId, item: T, index: number) => {
    const rawKey = valueField ? item?.[valueField] : undefined;
    const key = rawKey != null ? String(rawKey) : `${String(getText(item) ?? "item")}-${index}`;
    return (
      <div
        key={key}
        className={classNames(classes.listItem, {
          selected: selectionOf(listId).has(item),
          dragOver: dragOverItem === item,
        })}
        draggable
        onClick={(e) => handleItemClick(e, listId, item)}
        onDragStart={() => handleDragStart(listId, item)}
        onDragOver={(e) => handleItemDragOver(e, item)}
        onDrop={(e) => handleDropOnItem(e, listId, item)}
        onDragEnd={handleDragEnd}
      >
        {props.itemTemplate ? (
          props.itemTemplate(item)
        ) : (
          <span className={classes.itemText}>{String(getText(item) ?? "")}</span>
        )}
      </div>
    );
  };

  const renderColumn = (listId: ListId, header: string, visible: T[]) => {
    const query = queryOf(listId);
    const setQuery = listId === "available" ? setQueryAvailable : setQuerySelected;
    return (
      // minHeight:0 (fill mode) lets the box below shrink and scroll instead of stretching the
      // column; the equal column heights (align-items:stretch) give both boxes the same height.
      <div className={classes.column} style={fillHeight ? { minHeight: 0 } : undefined}>
        <div className={classes.header}>{header}</div>
        {showSearch && (
          <div className={classes.search}>
            <SearchGlyph />
            <input
              type="text"
              value={query}
              placeholder={searchPlaceholder}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && (
              <button type="button" className={classes.searchClear} title={t("Clear")} onClick={() => setQuery("")}>
                <ClearGlyph />
              </button>
            )}
          </div>
        )}
        <div
          className={classes.listBox}
          // fillHeight: flex to share the parent's bounded height equally with the sibling box and
          // scroll internally (listBox already sets overflow-y:auto). minHeight:0 lets it shrink
          // below its content so the scroll stays *inside* the box — the box never grows past the
          // modal, so its bottom shadow stays visible and the modal body itself never scrolls.
          // Otherwise it is a plain fixed-height box (listHeight), sized identically on both sides.
          style={fillHeight ? { flex: "1 1 0", minHeight: 0 } : { height: listHeight }}
          onDragOver={handleListDragOver}
          onDrop={(e) => handleDropOnList(e, listId)}
        >
          {visible.length ? (
            visible.map((item, index) => renderItem(listId, item, index))
          ) : (
            <div className={classes.emptyState}>
              <NoDataSvg text1={query ? t("No matches found") : noItemsText} />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={classes.root} style={fillHeight ? { flex: "1 1 0", minHeight: 0 } : undefined}>
      {showHint && <span className={classes.hint}>{t("Drag and drop or select to mark them as selected")}</span>}
      {/* fillHeight chains a bounded height down to the list boxes: root fills its flex parent, and
          lists fills the space left under the hint — each with minHeight:0 so the boxes (not this
          container) absorb overflow via their own scroll. */}
      <div className={classes.lists} style={fillHeight ? { flex: "1 1 0", minHeight: 0 } : undefined}>
        {renderColumn("available", availableHeader, visibleAvailable)}
        {showButtons && (
          <div className={classes.buttonColumn}>
            {buttons.map((b) => (
              <button
                key={b.key}
                type="button"
                className={classes.iconButton}
                title={b.title}
                disabled={b.disabled}
                onClick={b.onClick}
              >
                {b.node}
              </button>
            ))}
          </div>
        )}
        {renderColumn("selected", selectedHeader, visibleSelected)}
      </div>
    </div>
  );
}

export default DragListComponent;
